import SettingsIcon from '@mui/icons-material/Settings';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  ListItem,
  ListItemText,
  Switch,
  Button,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@sophocles/app';
import {
  buildIntegrationConfigPatch,
  isAuthTypeLocked,
  resolveAuthTypeKey,
} from '@sophocles/features/customers/integrationConfigForm';
import { AuthType } from '@sophocles/features/customers/integrations';
import {
  useCreateCustomerIntegrationMutation,
  usePatchCustomerIntegrationMutation,
} from '@sophocles/features/entities/customers';

const IntegrationListItem = ({ integration, customerId, onChange, divider = true }) => {
  const [saving, setSaving] = useState(false);

  const [createIntegration] = useCreateCustomerIntegrationMutation();
  const [patchIntegrationMutation] = usePatchCustomerIntegrationMutation();
  const [open, setOpen] = useState(false);
  const [overrideAuthType, setOverrideAuthType] = useState(null);
  const [form, setForm] = useState({
    url: integration.url || '',
    user: integration.user || '',
    token: integration.token || '',
    password: integration.password || '',
    client_id: integration.client_id || '',
    client_secret: integration.client_secret || '',
    oauth_token_url: integration.oauth_token_url || '',
  });
  const [original, setOriginal] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const { user, isStaff } = useUser();
  const { t } = useTranslation();

  const parentAuthType = integration.integration?.auth_type ?? null;
  const savedAuthType = integration.auth_type ?? null;
  const authTypeKey = resolveAuthTypeKey({
    parentAuthType,
    savedAuthType,
    selectedAuthType: overrideAuthType,
  });
  const authType = AuthType[authTypeKey];

  const handleToggle = async () => {
    setSaving(true);
    if (integration.id == null) {
      await createIntegration({
        customerId,
        body: { integration: integration.integration.id, enabled: true },
      });
    } else {
      await patchIntegrationMutation({
        customerId,
        integrationId: integration.id,
        body: { enabled: !integration.enabled },
      });
    }
    setSaving(false);
    onChange && onChange();
  };

  const handleOpen = () => {
    setOriginal({
      url: integration.url || '',
      user: integration.user || '',
      token: integration.token || '',
      password: integration.password || '',
      client_id: integration.client_id || '',
      client_secret: integration.client_secret || '',
      oauth_token_url: integration.oauth_token_url || '',
    });
    setOverrideAuthType(null);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleField = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    const patch = buildIntegrationConfigPatch({
      form,
      original,
      parentAuthType,
      savedAuthType,
      selectedAuthType: overrideAuthType,
    });
    await patchIntegrationMutation({ customerId, integrationId: integration.id, body: patch });
    setSaving(false);
    setOpen(false);
    onChange && onChange();
  };

  return (
    <ListItem
      divider={divider}
      sx={{
        width: '100%',
        // Match the px the bordered Subsection uses for non-flush
        // children so the divider runs edge-to-edge of the
        // surrounding card.
        px: { xs: 1.5, sm: 2 },
        py: 1.25,
      }}
    >
      <ListItemText primary={integration.integration.name} secondary={integration.integration.description} />
      <Box display="flex" alignItems="center" gap={2} ml="auto">
        {integration.id != null && (
          <IconButton onClick={handleOpen} aria-label={t('integrations.configureIntegrations')}>
            <SettingsIcon />
          </IconButton>
        )}
        <Switch
          checked={!!integration.enabled}
          onChange={handleToggle}
          disabled={saving}
          color="primary"
          inputProps={{ 'aria-label': t('integrations.enableIntegration') }}
        />
      </Box>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('integrations.configureIntegration')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="URL" name="url" value={form.url} onChange={handleField} fullWidth margin="dense" />
          {!isAuthTypeLocked(parentAuthType) ? (
            <FormControl fullWidth>
              <InputLabel id={`${integration.integration.id}-auth-label`}>{t('integrations.authType')}</InputLabel>
              <Select
                label={t('integrations.authType')}
                labelId={`${integration.integration.id}-auth-label`}
                value={authTypeKey ?? ''}
                onChange={e => setOverrideAuthType(e.target.value)}
                fullWidth
                margin="dense"
              >
                {Object.entries(AuthType).map(([key, value]) => (
                  <MenuItem key={key} value={Number(key)}>
                    {t(`authType.${value}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          {authType == 'basic' || authType == 'httpDigest' ? (
            <>
              <TextField label="User" name="user" value={form.user} onChange={handleField} fullWidth margin="dense" />
              <TextField
                label={t('auth.password')}
                name="password"
                value={form.password}
                onChange={handleField}
                fullWidth
                margin="dense"
                type={showPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                      onClick={() => setShowPassword(v => !v)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />
            </>
          ) : null}
          {authType == 'token' || authType == 'bearerToken' ? (
            <TextField
              label="Token"
              name="token"
              value={form.token}
              onChange={handleField}
              fullWidth
              margin="dense"
              type={showPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <IconButton
                    aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                    onClick={() => setShowPassword(v => !v)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
          ) : null}
          {authType == 'oauth' ? (
            <>
              <TextField
                label={t('integrations.clientId')}
                name="client_id"
                value={form.client_id}
                onChange={handleField}
                fullWidth
                margin="dense"
              />
              <TextField
                label={t('integrations.clientSecret')}
                name="client_secret"
                value={form.client_secret}
                onChange={handleField}
                fullWidth
                margin="dense"
                type={showPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                      onClick={() => setShowPassword(v => !v)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />
              <TextField
                label={t('integrations.oauthTokenUrl')}
                name="oauth_token_url"
                value={form.oauth_token_url}
                onChange={handleField}
                fullWidth
                margin="dense"
              />
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose} disabled={saving}>
            {t('actions.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {t('actions.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </ListItem>
  );
};

export default IntegrationListItem;
